import {
  ClearTimes, DailyEntry, DailyRoadState, EndlessBest, LevelModifiers, RaceWeekRecord, RecordsData, RecordsJson, RecordsState, ChallengeRecord, ChallengeResult, RecordNote,
  STARS_PER_LEVEL, isoWeekKey, starCount, utcDay, utcWeek,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import { GameEventHub, gameEvents } from "./gameEvents";
import { Endgame } from "./events";
import { Modes } from "./modes";
import type { LevelCatalog } from "./levelCatalog";

/**
 * The Hall of Records (MP-27, docs/MODES.md §8) as use cases: the `records` save section (domain RecordsState), filled
 * from the game events — `battleWon` (a campaign or farm win's clear time) and `raceFinished` (a Race Week time and
 * medal), each counted once by its key — plus the Endless bests read from the `modes` section (backfilled once, then
 * synced after each Endless run) and the challenge codes' results (app challenges.ts). The Hall's view also reads
 * Iron stars, level stars and the last Daily Road days live from their own sections.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const recordsSection = registerSaveSection<RecordsJson>("records", asObject, (v) => v);

export function recordsFromSave(save: HasSections): RecordsState { return RecordsState.fromJson(recordsSection.read(save)); }

/**
 * A run's mode id as `battleWon` names it: "challenge", "dailyRoad", "iron", "endless", "heroic", "trial", and for a
 * plain run "campaign" on its first clear, else "farm" (a cleared level replayed).
 */
export function battleMode(catalog: LevelCatalog, level: number, modifiers: LevelModifiers, firstClear: boolean): string {
  const mode = new Modes(catalog).runKind(modifiers);
  if (mode !== null) return mode === "daily" ? "dailyRoad" : mode;
  return new Endgame(catalog).runKind(level, modifiers) ?? (firstClear ? "campaign" : "farm");
}

/** One level's fastest clears (sim ms). */
export interface ClearRow { readonly level: number; readonly times: ClearTimes }

/** Everything the Hall of Records screen shows. */
export interface HallView {
  readonly endless: { readonly open: boolean; readonly best: EndlessBest; readonly week: EndlessBest; readonly weekKey: string };
  readonly iron: { readonly stars: number; readonly levels: number };
  readonly stars: { readonly have: number; readonly max: number; readonly cleared: number };
  readonly clears: readonly ClearRow[];
  /** The fastest clear of any level (campaign or farm), or null. */
  readonly fastest: { readonly level: number; readonly ms: number } | null;
  readonly daily: readonly DailyEntry[];
  readonly races: readonly RaceWeekRecord[];
  readonly medals: Readonly<Record<"gold" | "silver" | "bronze", number>>;
  readonly challenges: readonly ChallengeRecord[];
  /** Anything to show at all (Captain Bram's first line about the Hall waits for it). */
  readonly hasAny: boolean;
}

export class HallOfRecords {
  /**
   * `daily`: the Daily Road's kept days (the `dailyRoad` section, read on demand: it is that feature's state, never
   * copied here).
   */
  constructor(private readonly catalog: LevelCatalog, readonly state: RecordsState, private readonly daily: () => DailyRoadState, private readonly events: GameEventHub = gameEvents) {}

  get data(): RecordsData { return this.catalog.balance.modes.records; }

  /** Listens to `battleWon` and `raceFinished`; returns the unsubscribe. `changed` runs when a record moved (the caller saves). */
  attach(changed: () => void = () => undefined): () => void {
    const offWon = this.events.on("battleWon", (e) => { if (this.state.noteClear(e.mode, e.level, e.ms, e.key, this.data.keysKept).counted) changed(); });
    const offRace = this.events.on("raceFinished", (e) => { if (this.state.noteRace(e, this.data.keysKept, this.data.raceWeeksKept).counted) changed(); });
    return () => { offWon(); offRace(); };
  }

  /** The `modes` section's Endless bests (this ISO week's only while its week is this one). */
  private endlessFromModes(now: number): { best: EndlessBest; week: { week: string; waves: number; points: number } | null } {
    const m = this.catalog.modes, day = utcDay(now);
    return { best: { waves: m.bestWaves, points: m.bestPoints }, week: m.week === utcWeek(day) ? { week: isoWeekKey(day), waves: m.weekWaves, points: m.weekPoints } : null };
  }

  /** The one-time backfill from what the save already held (true the first time). */
  backfill(now: number): boolean {
    const e = this.endlessFromModes(now);
    return this.state.backfill(e.best, e.week);
  }

  /** After an Endless run: the `modes` section's bests into the Hall (it keeps them past the week's reset). */
  syncEndless(now: number): RecordNote<EndlessBest> {
    const e = this.endlessFromModes(now);
    if (e.week) { this.state.noteEndlessOverall(e.best); return this.state.noteEndless(e.week.week, e.week); }
    return this.state.noteEndlessOverall(e.best);
  }

  /** A finished challenge run (app challenges.ts). */
  noteChallenge(r: ChallengeResult): RecordNote<ChallengeRecord> {
    return this.state.noteChallenge(r, this.catalog.balance.modes.challenge.recentKept, this.data.keysKept);
  }

  /** The screen's numbers. */
  view(now: number): HallView {
    const c = this.catalog, modes = new Modes(c), s = this.state, day = utcDay(now), weekKey = isoWeekKey(day);
    let have = 0;
    for (let l = 1; l <= c.levelCount; l++) have += starCount(c.starsFor(l));
    const clears = s.clearedLevels.map((level) => ({ level, times: s.clearsOf(level) }));
    let fastest: HallView["fastest"] = null;
    for (const r of clears) for (const ms of Object.values(r.times)) if (ms !== undefined && (fastest === null || ms < fastest.ms)) fastest = { level: r.level, ms };
    const daily = this.daily().entries.filter((e) => e.day > day - this.data.dailyDays);
    const view = {
      endless: { open: modes.endlessOpen, best: s.endlessBest, week: s.endlessWeekBest(weekKey), weekKey },
      iron: { stars: c.modes.ironStarCount, levels: modes.ironLevels().length },
      stars: { have, max: c.levelCount * STARS_PER_LEVEL, cleared: c.highestLevelCleared },
      clears, fastest, daily, races: s.races, medals: s.medalCounts(), challenges: s.challenges,
    };
    return { ...view, hasAny: s.hasAny || view.iron.stars > 0 || daily.length > 0 };
  }
}
